import React, { useContext, useState } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import SavingsTypesContext from '@totem/components/administration/optimize/savingsTypes/savingsTypesContext';
import {
  DEFAULT_SAVINGS_TYPE,
  SavingsType,
} from '@totem/components/administration/optimize/savingsTypes/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

export const SavingsTypesExtra = () => {
  const { loading, saving, onAdd, onRefresh } = useContext(SavingsTypesContext);

  return (
    <Space>
      <Button
        icon={<ReloadOutlined />}
        onClick={onRefresh}
        disabled={loading || saving}
      >
        Refresh
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={onAdd}
        disabled={loading || saving}
      >
        Add savings type
      </Button>
    </Space>
  );
};

const SavingsTypesLayout = () => {
  const { loading, saving, document, onSave, onEdit } =
    useContext(SavingsTypesContext);
  const [pageSize, setPageSize] = useState<number>(20);
  const savingsTypes = document?.data ?? [];

  const move = (index: number, offset: number) => {
    const next = [...savingsTypes];
    const [moved] = next.splice(index, 1);
    next.splice(index + offset, 0, moved);

    onSave(next);
  };

  const remove = (index: number) => {
    const target = savingsTypes[index];
    Modal.confirm({
      title: `Delete "${target.name}"?`,
      content:
        'It will no longer be offered on Optimize tickets. Tickets that already use it keep it.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () =>
        onSave(savingsTypes.filter((_, position) => position !== index)),
    });
  };

  const columns: ColumnProps<SavingsType>[] = [
    {
      title: 'Order',
      key: 'order',
      width: 90,
      render: (_, __, index) => (
        <Space size={4}>
          <Button
            size="small"
            type="text"
            icon={<ArrowUpOutlined />}
            title="Move up"
            disabled={saving || index === 0}
            onClick={() => move(index, -1)}
          />
          <Button
            size="small"
            type="text"
            icon={<ArrowDownOutlined />}
            title="Move down"
            disabled={saving || index === savingsTypes.length - 1}
            onClick={() => move(index, 1)}
          />
        </Space>
      ),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <>
          <strong>{record.name}</strong>
          {record.value === DEFAULT_SAVINGS_TYPE && (
            <Typography.Text type="secondary"> · default</Typography.Text>
          )}
        </>
      ),
    },
    {
      title: 'Value stored on tickets',
      key: 'value',
      render: (_, record) => (
        <Typography.Text type="secondary">{record.value}</Typography.Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record, index) => (
        <Space size={12}>
          <Typography.Link disabled={saving} onClick={() => onEdit(index)}>
            Edit
          </Typography.Link>
          {record.value === DEFAULT_SAVINGS_TYPE ? (
            <Tooltip title="New Optimize tickets start with this savings type, so it can't be deleted.">
              <Typography.Text type="secondary">Delete</Typography.Text>
            </Tooltip>
          ) : (
            <Typography.Link
              type="danger"
              disabled={saving}
              onClick={() => remove(index)}
            >
              Delete
            </Typography.Link>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        The Savings Type options on Optimize tickets, in the order they are
        offered. Renaming or deleting a savings type doesn&apos;t change tickets
        that already use it.
      </Typography.Paragraph>
      <IBTable
        columns={columns}
        dataSource={savingsTypes}
        loading={loading || saving}
        rowKey={(record) => record.value}
        locale={{ emptyText: 'No savings types yet.' }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
      />
      {document?.updatedAt && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Last saved {dayjs(document.updatedAt).format('MMM D, YYYY h:mm A')}
          {document.updatedByName ? ` by ${document.updatedByName}` : ''}
        </Typography.Text>
      )}
    </>
  );
};

export default SavingsTypesLayout;
